import { Link } from "react-router-dom";
import { ArrowRight, Package } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { useTrackPageView } from "@/lib/analytics";
import KineticTitle, { HeroFade } from "@/components/books/KineticTitle";
import Reveal from "@/components/books/Reveal";
import BookCover from "@/components/books/BookCover";
import PrintInterestButton from "@/components/books/PrintInterestButton";
import BuyButton from "@/components/books/BuyButton";
import PriceBlock from "@/components/books/PriceBlock";
import { LEVEL3_BOOKS, LEVEL3_BUNDLE, LEVEL3_SERIES, LEVEL3_ACCENT as L } from "@/data/level3Books";

export default function Level3Bundle() {
  useTrackPageView("level3_bundle_view", { source: "direct" });
  useSEO({
    title: "LifeIQ Level 3 Complete 5-Book Life Design Series",
    description: LEVEL3_BUNDLE.description,
    canonicalPath: "/books/level3/complete-series",
  });

  return (
    <main>
      <section className="relative overflow-hidden" style={{ backgroundColor: L.navy }}>
        <span aria-hidden="true" className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl pointer-events-none" style={{ backgroundColor: `${L.gold}1E` }} />
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 pt-16 md:pt-20 pb-24 md:pb-32 relative">
          <HeroFade delay={0}>
            <div className="flex items-center gap-2 text-[12px] text-white/50 flex-wrap">
              <Link to="/books/level3" className="hover:text-white transition-colors">LifeIQ Level 3 · Life Design</Link>
              <span>/</span>
              <span className="text-white/80">Complete Series</span>
            </div>
          </HeroFade>
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <HeroFade delay={0.1}>
                <div className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: L.gold }}>
                  <Package size={14} /> Complete series
                </div>
              </HeroFade>
              <KineticTitle
                testId="level3-bundle-hero-title"
                className="mt-4 font-serif-d text-[32px] sm:text-5xl lg:text-[50px] leading-[1.08] text-white"
                lines={["The Complete", "Level 3 · Life Design Series"]}
              />
              <HeroFade delay={0.25}>
                <div className="mt-5 flex flex-wrap gap-x-5 gap-y-1 text-[13px] sm:text-[14px] uppercase tracking-[0.1em] font-bold text-white/80">
                  <span>{LEVEL3_BUNDLE.totalBooks} Digital Books</span>
                  <span>·</span>
                  <span>{LEVEL3_BUNDLE.totalPages} PDF Pages</span>
                </div>
                <div className="mt-6 flex items-center gap-1.5 flex-wrap">
                  {LEVEL3_SERIES.progression.map((step, i) => (
                    <span key={step} className="flex items-center gap-1.5">
                      <span className="text-[11px] font-bold uppercase tracking-[0.08em] px-2.5 py-1.5 rounded-sm border" style={{ color: "white", borderColor: "rgba(255,255,255,0.22)" }}>
                        {step}
                      </span>
                      {i < LEVEL3_SERIES.progression.length - 1 && <ArrowRight size={11} className="text-white/35" />}
                    </span>
                  ))}
                </div>
                <p className="mt-6 text-[16px] sm:text-[17px] text-white/80 leading-[1.7] max-w-xl">{LEVEL3_BUNDLE.description}</p>
                <div className="mt-6">
                  <PriceBlock product={LEVEL3_BUNDLE} dark big testId="level3-bundle-price-block" accentColor={L.gold} offerChipColor={L.gold} regularPriceLabel="Regular Combined Price" savings={LEVEL3_BUNDLE.savings} />
                </div>
                <p data-testid="level3-bundle-value-note" className="mt-3 text-[12px] text-white/45 leading-[1.6] max-w-md">
                  Buying the five books individually at current launch prices: ₹{LEVEL3_BUNDLE.individual_launch_total}. Complete Series: {LEVEL3_BUNDLE.price}. Additional bundle saving: ₹{LEVEL3_BUNDLE.bundle_extra_saving}.
                </p>
                <div className="mt-5">
                  <BuyButton
                    product={LEVEL3_BUNDLE}
                    label={`Get All 5 Books — ${LEVEL3_BUNDLE.price}`}
                    source="level3-bundle-page-hero"
                    testId="level3-bundle-buy-button"
                    dark
                    accentColor={L.gold}
                  />
                </div>
                <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-3 text-[13px]">
                  <span className="text-white/40 uppercase tracking-wide text-[11px] font-semibold">Printed Edition · Planned</span>
                  <PrintInterestButton
                    mode="single"
                    seriesId="level3"
                    productId={LEVEL3_BUNDLE.product_id}
                    bookTitle={LEVEL3_BUNDLE.title}
                    sourcePage="level3-bundle-pdp"
                    testId="level3-bundle-print-interest"
                    accentColor={L.gold}
                    className="text-white/70 hover:text-white underline underline-offset-2 text-[13px] font-semibold transition-colors"
                  />
                </div>
              </HeroFade>
            </div>
            <div className="lg:col-span-5">
              <HeroFade delay={0.3} className="grid grid-cols-3 gap-3 max-w-[440px] mx-auto">
                {LEVEL3_BOOKS.map((b) => <BookCover key={b.slug} book={b} tilt testId={`level3-bundle-cover-${b.slug}`} />)}
              </HeroFade>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section style={{ backgroundColor: L.cream }}>
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-20 md:py-28">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: L.teal }}>What's included</div>
            <h2 className="mt-4 font-serif-d text-[28px] sm:text-4xl leading-[1.1]" style={{ color: L.navy }}>All five books, one journey.</h2>
          </Reveal>
          <div className="mt-12 space-y-6" data-testid="level3-bundle-books-list">
            {LEVEL3_BOOKS.map((b) => (
              <Reveal key={b.slug} delay={b.book_number * 0.05}>
                <div className="grid grid-cols-12 gap-6 items-center border rounded-sm p-6" style={{ borderColor: `${L.navy}14`, backgroundColor: "white" }}>
                  <div className="col-span-3 sm:col-span-2">
                    <Link to={`/books/level3/${b.slug}`}>
                      <BookCover book={b} className="max-w-[110px]" />
                    </Link>
                  </div>
                  <div className="col-span-9 sm:col-span-10">
                    <div className="text-[11px] uppercase tracking-[0.16em] font-semibold" style={{ color: b.accent }}>{b.label} · {b.series_step}</div>
                    <Link to={`/books/level3/${b.slug}`} className="mt-1 block font-serif-d text-[19px] hover:underline" style={{ color: L.navy }}>{b.title}</Link>
                    <p className="mt-1 text-[13px]" style={{ color: L.textMuted }}>{b.subtitle} · {b.pages} pages</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
